import React, { useContext } from 'react'
import { FixedSpacer, Typography } from '@grammarly/badger-ui'
import { SocialShareWidget } from '@components/share-widget/social-share-widget'
import { CtaButtonDescription } from '@components/item/cta-button'
import { ContentProps, ContentRenderer } from '@components/renderer'
import { renderRichTextMultiLineContent } from '@components/renderer/rich-text'
import { SectionContext } from '@contexts/section-context'
import { FormattedMessage } from 'react-intl'
import { useGetAuthAndDeviceTypeCta } from '@lib/hooks/auth-and-device-type-cta'
import { TypeSectionHero } from '@lib/types'
import { isUnresolvedLink } from '@lib/utils/contentful-entry'
import { isTypeComponentEmptyStub } from '@lib/types/content-types'
import { getDescriptionComponent } from './title'

import heroStyles from './hero.module.scss'
import grammarlyAtWork25Styles from '@components/dynamic/grammarly-at-work-25/grammarly-at-work-25.module.scss'

/**
 * Grammarly at Work 25 Hero Title Component
 * Custom hero title matching the Figma design with static styling
 */
export const GrammarlyAtWork25HeroTitle: React.FC<ContentProps<TypeSectionHero>> = (props) => {
  const {
    structure: {
      fields: { title, titleSize, seoTitle, description, ctAs, socialShareWidget },
    },
  } = props

  const { isH1Section } = useContext(SectionContext)

  const { getAuthAndDeviceTypeCta } = useGetAuthAndDeviceTypeCta()

  const EyebrowTitleComponent = Typography.C6
  // Use T1 for consistency with animated hero, regardless of titleSize prop
  const TitleComponent = Typography.T1
  const DescriptionComponent = getDescriptionComponent(titleSize)

  return (
    <div className={grammarlyAtWork25Styles.grammarlyHeroTitleWrapper}>
      <figcaption className={grammarlyAtWork25Styles.grammarlyHeroTitle}>
        {/* Left Section - Title Area */}
        <div className={grammarlyAtWork25Styles.grammarlyTitleSection}>
          <div className={grammarlyAtWork25Styles.grammarlyBadge}>
            <span>
              <FormattedMessage
                id="hero.grammarlyAtWork25HeroTitle.badgeText"
                description="Hero - Grammarly At Work 25 Hero Title - Badge Text"
                defaultMessage="New"
              />
            </span>
          </div>
          {seoTitle ? (
            <>
              <EyebrowTitleComponent as={isH1Section ? 'h2' : 'h3'} className={heroStyles.seoTitle}>
                {seoTitle}
              </EyebrowTitleComponent>
              <TitleComponent as="h1" className={grammarlyAtWork25Styles.grammarlyTitle}>
                {renderRichTextMultiLineContent(title)}
              </TitleComponent>
            </>
          ) : (
            <TitleComponent as="h1" className={grammarlyAtWork25Styles.grammarlyTitle}>
              {renderRichTextMultiLineContent(title)}
            </TitleComponent>
          )}
        </div>

        {/* Right Section - Content Area */}
        <div className={grammarlyAtWork25Styles.grammarlyContentSection}>
          <DescriptionComponent as="div" className={grammarlyAtWork25Styles.grammarlyDescription}>
            {renderRichTextMultiLineContent(description)}
          </DescriptionComponent>

          {ctAs?.length && (
            <>
              <div className={grammarlyAtWork25Styles.grammarlyCtaContainer}>
                <ContentRenderer structure={ctAs} />
              </div>
              <div className={grammarlyAtWork25Styles.grammarlyCtaDescriptionsContainer}>
                {ctAs
                  .map(getAuthAndDeviceTypeCta)
                  .filter((cta) => !isUnresolvedLink(cta))
                  .filter((cta) => !isTypeComponentEmptyStub(cta))
                  .map((cta) => (
                    <CtaButtonDescription
                      key={`description-${cta.sys.contentType.sys.id}-${cta.sys.id}`}
                      structure={cta}
                    />
                  ))}
              </div>
            </>
          )}

          {socialShareWidget && (
            <>
              <FixedSpacer multiplier={2} />
              <SocialShareWidget structure={socialShareWidget} />
            </>
          )}
        </div>
      </figcaption>
    </div>
  )
}
